import React, { useEffect, useState } from 'react';
import BEM from '@upsales/components/Utils/bemClass';
import { Card, Flex, Title, Text, Icon, type IconName } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import logError from 'Helpers/logError';
import OutreachStepRow from 'Components/OutreachActions/OutreachStepRow';
import { useOutreachSteps } from 'Components/OutreachActions/useOutreachSteps';
import { unwrap } from 'Components/OutreachActions/types';

import './ContactOutreachPlan.scss';

type ContactEngagementStep = {
	planId: number;
	day: number;
	channel: string;
	type: string;
	subject: string | null;
	status: string;
};

type Props = {
	contactId: number;
};

// A step is "done" only once the rep has carried out the activity ('completed').
// 'sent' means the to-do was merely scheduled (WEB-8734) — still outstanding — so a
// scheduled step stays visible; a plan hides only when every step is completed.
const DONE_STATUSES = new Set(['completed']);

const CHANNEL_ICON: Record<string, IconName> = {
	email: 'envelope',
	call: 'phone',
	linkedin: 'linkedin',
	todo: 'check-square-o'
};

/**
 * The outreach aimed at this contact (WEB-8617), and the reaches the rep can trigger
 * from it (WEB-8739).
 *
 * Collapsed it's a one-line reference to the plan; expanding it lists this person's
 * approved-but-not-done reaches as their channel's action, the same rows the account
 * card shows. A contact whose steps are all pending or already done has nothing to
 * trigger, so the read-only sequence stands in — that's the WEB-8617 detail view.
 */
const ContactOutreachPlan = ({ contactId }: Props) => {
	const { t } = useTranslation();
	const [steps, setSteps] = useState<ContactEngagementStep[] | null>(null);
	const [expandedPlanId, setExpandedPlanId] = useState<number | null>(null);
	const [expandedStepId, setExpandedStepId] = useState<number | null>(null);
	const { steps: actionable, busyStepId, recordDone, sendEmail } = useOutreachSteps({ contactId });

	useEffect(() => {
		if (!contactId || contactId <= 0) {
			setSteps(null);
			return;
		}
		let cancelled = false;
		fetch(`/api/v2/prospectingAgentRuns/contact/${contactId}/engagementSteps`)
			.then(r => r.json())
			.then(body => {
				if (cancelled) return;
				const rows = unwrap<ContactEngagementStep[]>(body);
				setSteps(Array.isArray(rows) ? rows : []);
			})
			.catch(err => {
				logError(err, 'ContactOutreachPlan: failed to load engagement steps');
			});
		return () => {
			cancelled = true;
		};
	}, [contactId]);

	// Reuse the review page's channel/status keys, falling back to the raw value.
	const labelFor = (prefix: string, value: string): string => {
		const key = `prospectingAgent.${prefix}.${value}`;
		const translated = t(key);
		return translated && translated !== key ? translated : value;
	};

	// Group the flat step list by plan (newest first, preserving the day order the
	// API returns), then drop any plan whose steps are all done.
	const groups: { planId: number; steps: ContactEngagementStep[] }[] = [];
	for (const step of steps ?? []) {
		const last = groups[groups.length - 1];
		if (last && last.planId === step.planId) {
			last.steps.push(step);
		} else {
			groups.push({ planId: step.planId, steps: [step] });
		}
	}
	const visible = groups.filter(group => group.steps.some(step => !DONE_STATUSES.has(step.status)));

	if (!visible.length) {
		return null;
	}

	const CSS = new BEM('ContactOutreachPlan');

	return (
		<div className={CSS.b()}>
			{visible.map(group => {
				const planActionable = actionable.filter(step => step.planId === group.planId);
				const canExpand = planActionable.length > 0;
				const expanded = expandedPlanId === group.planId;

				return (
					<Card key={group.planId} className={CSS.elem('card').mod({ expandable: canExpand }).b()}>
						<div
							className={CSS.elem('header').b()}
							onClick={() => canExpand && setExpandedPlanId(expanded ? null : group.planId)}
						>
							<Flex justifyContent="space-between" alignItems="center" gap={8}>
								<Flex alignItems="center" gap={8}>
									<Icon name="paper-plane" color="bright-blue" />
									<Title size="sm">{t('prospectingAgent.engagementPlan')}</Title>
									{canExpand ? (
										<Text size="sm" color="grey-10">
											{t('prospectingAgent.stepsReadyToReachOut', {
												count: planActionable.length
											})}
										</Text>
									) : null}
								</Flex>
								<Flex alignItems="center" gap={8}>
									<a
										className={CSS.elem('open').b()}
										href={`#/prospecting-agent?plan=${group.planId}&from=plans`}
										onClick={e => e.stopPropagation()}
									>
										{t('prospectingAgent.openPlan')}
									</a>
									{canExpand ? (
										<Icon name={expanded ? 'chevron-up' : 'chevron-down'} color="grey-8" />
									) : null}
								</Flex>
							</Flex>
						</div>

						{expanded ? (
							<div className={CSS.elem('steps').b()}>
								{planActionable.map(step => (
									<OutreachStepRow
										key={step.stepId}
										step={step}
										busy={busyStepId === step.stepId}
										expanded={expandedStepId === step.stepId}
										onToggle={() =>
											setExpandedStepId(expandedStepId === step.stepId ? null : step.stepId)
										}
										onSend={() => sendEmail(step)}
										onDone={() => recordDone(step)}
									/>
								))}
							</div>
						) : null}

						{/* Nothing to trigger — show what the agent planned instead, which is the
						    read-only sequence WEB-8617 put here. */}
						{canExpand ? null : (
							<div className={CSS.elem('steps').b()}>
								{group.steps.map((step, i) => (
									<Flex key={i} alignItems="center" gap={10} className={CSS.elem('step').b()}>
										<Icon name={CHANNEL_ICON[step.channel] || 'circle'} color="grey-10" />
										<div className={CSS.elem('step-main').b()}>
											<Text size="sm" ellipsis={true}>
												{step.subject || labelFor('channel', step.channel)}
											</Text>
											<Text size="sm" color="grey-10">
												{/* The catalog value uses a bare {n}, not i18next's
												    {{n}} — substitute it the way this card always has. */}
												{t('prospectingAgent.stepDay').replace('{n}', String(step.day))}
												{' · '}
												{labelFor('engagementStatus', step.status)}
											</Text>
										</div>
									</Flex>
								))}
							</div>
						)}
					</Card>
				);
			})}
		</div>
	);
};

export default ContactOutreachPlan;
